import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTour } from "@reactour/tour";
import MainMenuSidebar from "./MainMenuSidebar";
import { MenuIcon } from "./NavigationIcons";
import FilterIconOverlay from "@modules/Projects/pages/ProjectDetail/ProjectDetailMapView/FilterIconOverlay";
import "./dual-sidebar.css";

const DualSidebarNav = ({ onLogout }) => {
  const { mobile } = useSelector((state) => state.mobile);
  const location = useLocation();
  const [mainMenuOpen, setMainMenuOpen] = useState(!mobile);
  const [projectSidebarOpen, setProjectSidebarOpen] = useState(false);
  const { t } = useTranslation();
  const toggleBtnRef = React.useRef(null);
  const { isOpen: isTourOpen } = useTour();

  // Check if current page is MapView - hide navigation sidebars
  // Commented out: Map is now part of Tree Cadastre menu, so navigation should work
  // const isFullScreenPage = location.pathname === "/map";
  const isFullScreenPage = false; // No full-screen pages currently

  // Check if on datascience project page - keep sidebars available but closed by default
  const isDataSciencePage = location.pathname.startsWith("/data-science");

  useEffect(() => {
    if (mobile) {
      setMainMenuOpen(false);
      setProjectSidebarOpen(false);
    } else if (isDataSciencePage) {
      // On datascience page: keep sidebars available but closed
      setMainMenuOpen(false);
      setProjectSidebarOpen(false);
    } else if (!isTourOpen) {
      // On other pages: open main menu by default (but not during a tour)
      setMainMenuOpen(true);
    }
  }, [mobile, isDataSciencePage, isTourOpen]);

  // Close both sidebars when navigating to full-screen pages
  useEffect(() => {
    if (isFullScreenPage) {
      setMainMenuOpen(false);
      setProjectSidebarOpen(false);
    }
  }, [isFullScreenPage]);

  const handleMainMenuToggle = () => {
    if (!mainMenuOpen) {
      // Opening main menu, close project sidebar
      setProjectSidebarOpen(false);
    }
    setMainMenuOpen(!mainMenuOpen);
  };

  return (
    <>
      {/* Toggle buttons in the blue header */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          height: "4rem",
          display: "flex",
          alignItems: "center",
          paddingLeft: mobile ? "0.5rem" : "1rem",
          gap: mobile ? "0.5rem" : "0.75rem",
        }}
      >
        <button
          ref={toggleBtnRef}
          className="sidebar-toggle-btn nav-link text-white d-inline-flex align-items-center fw-bold"
          onClick={handleMainMenuToggle}
          title={t("menu")}
          aria-label={t("menu")}
          style={{
            background: "transparent",
            border: "none",
            padding: "0.35rem 0.75rem",
            borderRadius: "0.55rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
            boxShadow: "none",
            width: "auto",
            height: "auto",
            minHeight: "2.5rem",
            fontWeight: 700,
            fontSize: "0.9375rem",
            lineHeight: 1.2,
          }}
        >
          <MenuIcon size={20} className="menu-toggle-icon" />
          {!mobile && <span>{t("menu")}</span>}
        </button>
        <FilterIconOverlay mode="inline" placement="right" />
      </div>

      {/* Sidebars - don't render on full-screen pages */}
      {!isFullScreenPage && (
        <>
          <MainMenuSidebar
            isOpen={mainMenuOpen}
            isMobile={mobile}
            onClose={() => setMainMenuOpen(false)}
            toggleButtonRef={toggleBtnRef}
            onLogout={onLogout}
          />

          {/* Mobile overlays */}
          {mobile && mainMenuOpen && (
            <div className="sidebar-overlay" onClick={() => setMainMenuOpen(false)} />
          )}
        </>
      )}
    </>
  );
};

export default DualSidebarNav;
